import { useEffect } from 'react';
import type { ApprovalsState, CostsState } from '@orgus/protocol';
import { formatClock, plural, toast } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { flushQueue } from './decisions.ts';
import { demoApprovals, demoNotify } from './demo.ts';
import { buildMockCosts, useMockCosts } from '../costs/mock.ts';
import { isApprovalsState, isCostsState } from './model.ts';
import { saveSnapshot, usePwa } from './pwa.ts';
import { mockControl } from '../control/live.ts';
import { useMe } from '../auth/useMe.ts';

/** state.ext.approvals, or undefined when the approvals module is not running on this hub. */
export const useApprovals = (): ApprovalsState | undefined =>
  useOrgus((s) => {
    const v = s.ext?.approvals;
    return isApprovalsState(v) ? v : undefined;
  });

/** state.ext.costs, or undefined when the costs module is not running on this hub. */
export const useCosts = (): CostsState | undefined =>
  useOrgus((s) => {
    const v = s.ext?.costs;
    return isCostsState(v) ? v : undefined;
  });

/** Demo mode: fill in module data the simulator does not have (skip with ?ext=none). */
export function useDemoExt(): void {
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  useEffect(() => {
    if (mode !== 'mock' || phase !== 'ready') return;
    if (new URLSearchParams(window.location.search).get('ext') === 'none') return;
    const now = Date.now();
    // Through applyExt, so the slices land in the full state and survive the next update of
    // the scoped view (setting `ext` directly was wiped by the first simulator tick).
    const st = useOrgus.getState();
    if (!st.ext.approvals) st.applyExt({ ext: 'approvals', type: 'state', data: demoApprovals(now) });
    // The Spend page's demo data, so phone and console show the same numbers.
    if (!st.ext.costs) st.applyExt({ ext: 'costs', type: 'state', data: buildMockCosts(useOrgus.getState(), now, useMockCosts.getState().budgets, useMockCosts.getState().billing) });
    if (!st.ext.notify) st.applyExt({ ext: 'notify', type: 'state', data: demoNotify(now) });
    // Session control as on the console's demo: the first machine controlled, so a session can be messaged.
    if (!st.ext.control) st.applyExt({ ext: 'control', type: 'state', data: mockControl(Object.values(st.machines), Object.values(st.sessions), now) });
  }, [mode, phase]);
}

/**
 * Ask the hub who we are. The phone layout is not inside the console's AuthGate, so without
 * this every role check (useCan) stayed false and actions were disabled with no reason shown.
 */
export function useWhoAmI(): void {
  const loading = useOrgus((s) => s.phase === 'loading');
  useEffect(() => {
    if (loading) return;
    if (useMe.getState().status === 'loading') void useMe.getState().load();
  }, [loading]);
}

/** Keep the last state in Cache Storage (at most every 15 s) for offline viewing. */
export function useSnapshot(): void {
  useEffect(() => {
    let last = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const save = () => {
      timer = null;
      last = Date.now();
      const s = useOrgus.getState();
      if (s.mode === 'live' && s.phase === 'ready' && s.connection === 'live') void saveSnapshot(s).catch(() => undefined);
    };
    const unsub = useOrgus.subscribe(() => {
      if (timer) return;
      timer = setTimeout(save, Math.max(0, 15_000 - (Date.now() - last)));
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, []);
}

/** Send decisions queued offline when the network is back, and say what happened to each. */
export function useDecisionQueue(): void {
  const offline = usePwa((s) => s.offline);
  const mode = useOrgus((s) => s.mode);
  useEffect(() => {
    if (offline || mode !== 'live') return;
    let cancelled = false;
    void flushQueue()
      .then((r) => {
        if (cancelled) return;
        if (r.sent.length) toast(`Sent ${plural(r.sent.length, 'decision')} made offline`, { tone: 'success' });
        for (const d of r.expired)
          toast(`Not sent: "${d.title}" expired`, { tone: 'error', description: `It stopped accepting answers at ${formatClock(d.expiresAt)}. The session got its timeout answer.` });
        for (const x of r.refused) toast(`Not sent: "${x.d.title}"`, { tone: 'error', description: x.why });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [offline, mode]);

  // The service worker reports what happened to Approve and Deny pressed on a notification.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const m = e.data as { type?: string; ok?: boolean; title?: string; message?: string } | null;
      if (m?.type !== 'decision') return;
      toast(m.ok ? `Decided: ${m.title ?? 'request'}` : `Not sent: ${m.title ?? 'request'}`, { tone: m.ok ? 'success' : 'error', description: m.message });
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, []);
}
